import { useState } from "react";
import type { User } from "@ferrivia/api-contract";
import { useTracker } from "@ferrivia/auth-web";
import { FloatingTab, FloatingTabBar } from "@ferrivia/ui";
import { useTranslation } from "@ferrivia/i18n";
import { NavLink } from "react-router";
import { AccountSheet } from "../AccountSheet";
import { NAV_DESTINATIONS } from "../../nav";
import { initials } from "../../user";
import s from "./TabBar.module.css";

export interface TabBarProps {
  user: User;
  onSignOut: () => void;
}

export function TabBar({ user, onSignOut }: TabBarProps) {
  const { t } = useTranslation("clinic");
  const tracker = useTracker();
  const [accountOpen, setAccountOpen] = useState(false);

  function openAccount() {
    tracker.track("clinic.account_sheet_opened", {});
    setAccountOpen(true);
  }

  const labels: Record<string, string> = {
    "nav.queue": t("nav.queue", "Queue"),
    "nav.customers": t("nav.customers", "Customers"),
    "nav.tasks": t("nav.tasks", "Tasks"),
  };

  return (
    <FloatingTabBar className={s.root} aria-label={t("nav.sectionsLabel", "Sections")}>
      {NAV_DESTINATIONS.map(({ path, labelKey, icon: Icon }) => (
        <FloatingTab
          key={path}
          as={NavLink}
          to={path}
          icon={<Icon aria-hidden size={22} strokeWidth={1.75} />}
          label={labels[labelKey]}
        />
      ))}
      {/* The nav rail — and with it the only sign-out — is hidden below 960px,
          so the account sheet hangs off the tab bar instead. */}
      <FloatingTab
        as="button"
        type="button"
        aria-haspopup="dialog"
        onClick={openAccount}
        icon={
          <span className={s.avatar} aria-hidden="true">
            {initials(user)}
          </span>
        }
        label={t("tabBar.account", "Account")}
      />

      <AccountSheet user={user} open={accountOpen} onOpenChange={setAccountOpen} onSignOut={onSignOut} />
    </FloatingTabBar>
  );
}
